import { useState, FormEvent, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import ModalComponent from "components/modal";
import { emptyExpenseRow, ExpenseItem } from "../detail";
import ActionButton from "components/table/action-btn";
import { IconInfoCircle, IconMinus } from "@tabler/icons-react";
import { getLocationsByUser } from "api/location";
import { getTemplatesByLocation } from "api/template";
import { LevelStructureType, LevelType, LocationType, Template } from "types/dict";
import { useSharedState } from "state";
import { createPaymentRequest, uploadPaymentFiles } from "api/payment";
import { getBudgetBalance } from "api/budget_request";
import FormattedNumberInput from "components/input/formatted-number";
import moment from "moment";
import { getLevelTreeStructure } from "api/level";
import { getDocumentsTableList } from "api/document";
import { DocumentFilters, DocumentProps } from "types/document";

type FormErrors = Record<string, boolean>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = [
  "location_id",
  "company_id",
  "department_id",
  "template_id",
  "contragent_id",
  "project",
  "year",
  "title",
  "vid_oplaty",
  "opisanie_rasxoda",
  "file",
] as const;

const PAYMENT_TYPES = [
  {
    value: "Безналичная оплата",
    label: "Безналичная оплата",
  },
  {
    value: "Наличная оплата",
    label: "Наличная оплата",
  },
];

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

interface NewPaymentProps {
  onClose?: () => void;
}

export default function NewPayment({ onClose }: NewPaymentProps = {}) {
  const {
    user,
    isAdmin,
    budgetTypes,
    getBudgetTypesData,
    getPaymentsData,
    getPaymentsTableData,
    companies,
    getCompaniesData,
    departments,
    getDepartmentsData,
    contractors,
    getContractorsData,
    levelRoots,
    getLevelRootsData,
    copyData,
    setCopyData,
    notify,
  } = useSharedState();
  const { locationName } = useParams();

  const location = useLocation();
  const locationCopyData = location.state?.copyData;

  const finalCopyData = copyData || locationCopyData;
  const [formRecords, setFormRecords] = useState<FormRecords>({ year: moment().year().toString() });
  const [expenseTable, setExpenseTable] = useState<ExpenseItem[]>(finalCopyData?.expenseTable || [emptyExpenseRow]);
  // Store available levels for each expense row and each level
  const [availableLevelsByRow, setAvailableLevelsByRow] = useState<{ [rowIdx: number]: LevelType[][] }>({});

  const [totalBudgetBalance, setTotalBudgetBalance] = useState<number>(0);

  const [errors, setErrors] = useState<FormErrors>({});
  const [fileList, setFileList] = useState<FileList | null>(null);

  const [userTemplates, setUserTemplates] = useState<Template[]>([]);
  const [completedDocuments, setCompletedDocuments] = useState<DocumentProps[]>([]);

  const [isBudgetCalculated, setBudgetCalculated] = useState<boolean>(false);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [withoutContract, setWithoutContract] = useState<boolean>(false);

  const totalSum = useMemo(
    () => expenseTable.reduce((acc, cur) => acc + (cur.quantity || 0) * (cur.price || 0), 0),
    [expenseTable]
  );

  const maxLevels = useMemo(() => {
    // Find the largest max_levels in expenseTable by budget.max_levels property
    return Math.max(
      0,
      ...expenseTable.map((item) => {
        // Find the matching level by budget_type_id
        const level = levelRoots.find((lvl) => lvl.budget_type_id === Number(item.budget));
        return level?.max_levels ?? 0;
      })
    );
  }, [expenseTable, levelRoots]);

  const isBudgetCalculable = useMemo(() => {
    return !!formRecords.location_id && !!formRecords.company_id && !!formRecords.department_id && !!formRecords.year;
  }, [formRecords]);

  const paymentFields = [
    {
      id: "location_id",
      title: "Филиал",
      type: "select",
      placeholder: "Выберите филиал",
      options: [
        ...user.locations
          .filter((loc) => loc.status)
          .map((loc) => ({ value: String(loc.location_id), label: loc.location })),
      ],
      disabled: undefined,
    },
    {
      id: "company_id",
      title: "Юр. лицо",
      type: "select",
      placeholder: "Выберите юр. лицо",
      options: [
        ...companies
          .filter((com) => com.location_id === +formRecords.location_id && com.status)
          .map((com) => ({ value: String(com.company_id), label: com.company })),
      ],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "department_id",
      title: "Отдел",
      type: "select",
      placeholder: "Выберите отдел",
      options: [
        ...departments
          .filter((dep) => dep.status)
          .map((dep) => ({ value: String(dep.department_id), label: dep.department })),
      ],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "template_id",
      title: "Шаблон",
      type: "select",
      placeholder: formRecords.location_id ? "Выберите шаблон" : "Сначала выберите филиал",
      options: [...userTemplates.map((temp) => ({ value: String(temp.template_id), label: temp.name }))],
      disabled: formRecords.location_id ? false : true,
    },
    {
      id: "project",
      title: "Проект",
      type: "text",
      placeholder: "Проект",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "year",
      title: "Год бюджета",
      type: "number",
      placeholder: "Год бюджета",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "title",
      title: "Наименование",
      type: "text",
      placeholder: "Наименование",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "contragent_id",
      title: "Контрагент",
      type: "select",
      placeholder: "Выберите контрагента",
      options: [
        ...contractors
          .filter((contr) => contr.status)
          .map((contr) => ({ value: String(contr.contragent_id), label: contr.contragent })),
      ],
      disabled: undefined,
    },
    {
      id: "document_request_id",
      title: "Номер договора",
      type: "select",
      placeholder: withoutContract
        ? ""
        : formRecords.contragent_id
        ? "Выберите договор"
        : "Сначала выберите контрагента",
      options: [
        ...completedDocuments.map((doc) => ({
          value: String(doc.document_request_id),
          label: doc.lease_number,
        })),
      ],
      disabled: !!withoutContract || !!!formRecords.contragent_id,
    },
    {
      id: "vid_oplaty",
      title: "Вид оплаты",
      type: "select",
      placeholder: "Выберите вид оплаты",
      options: PAYMENT_TYPES,
      disabled: undefined,
    },
    {
      id: "budget_summa",
      title: "Общая сумма бюджета",
      type: "number",
      placeholder: "Общая сумма бюджета",
      options: undefined,
      disabled: true,
    },
    {
      id: "ostatok_budgeta",
      title: "Общий остаток бюджета",
      type: "number",
      placeholder: "Общий остаток бюджета",
      options: undefined,
      disabled: true,
    },
    {
      id: "opisanie_rasxoda",
      title: "Описание расхода",
      type: "textarea",
      placeholder: "Описание расхода",
      options: undefined,
      disabled: undefined,
    },
    {
      id: "file",
      title: "Загрузить договоры",
      type: "file",
      placeholder: undefined,
      options: undefined,
      disabled: undefined,
    },
  ] as const;

  useEffect(() => {
    getBudgetTypesData();
    getLevelRootsData();
    getContractorsData();
  }, []);

  useEffect(() => {
    if (copyData && "year" in copyData) {
      setFormRecords((prev) => ({
        ...prev,
        location_id: copyData.location_id?.toString() || "",
        company_id: copyData.company_id?.toString() || "",
        department_id: copyData.department_id?.toString() || "",
        template_id: copyData.template_id?.toString() || "",
        project: copyData.project || "",
        year: copyData.year?.toString() || "",
        title: copyData.title || "",
        contragent_id: copyData.contragent_id?.toString() || "",
        document_request_id: copyData.document_request_id?.toString() || "",
        vid_oplaty: copyData.vid_oplaty || "",
        budget_summa: copyData.budget_summa?.toString() || "",
        ostatok_budgeta: copyData.ostatok_budgeta?.toString() || "",
        opisanie_rasxoda: copyData.opisanie_rasxoda || "",
      }));
    }
  }, [copyData, user, companies, departments, userTemplates, contractors, completedDocuments]);

  useEffect(() => {
    if (formRecords.contragent_id) {
      getCompletedDocumentsData();
    }
  }, [formRecords.contragent_id]);

  useEffect(() => {
    if (formRecords.location_id) {
      getTemplatesData();
      getCompaniesData();
      getDepartmentsData();
    }
  }, [formRecords.location_id]);

  useEffect(() => {
    if (withoutContract) {
      setFormRecords((prev) => ({ ...prev, document_request_id: "" }));
    }
  }, [withoutContract]);

  useEffect(() => {
    if (!isAdmin && formRecords.location_id) {
      const company = companies.find((c) => c.location_id === Number(formRecords.location_id));
      setFormRecords((prev) => ({ ...prev, company_id: company?.company_id.toString() || "" }));
      setFormRecords((prev) => ({ ...prev, department_id: user.result.department_id.toString() }));
    }
  }, [user, isAdmin, formRecords.location_id]);

  const getTemplatesData = async () => {
    try {
      const userTemplates = await getTemplatesByLocation("Оплаты", +formRecords.location_id);
      setUserTemplates(userTemplates);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении шаблонов");
    }
  };

  const getLevelTreeStructureData = async (parentId: number) => {
    try {
      const res: LevelStructureType = await getLevelTreeStructure(Number(parentId));
      return res;
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при загрузке структуры уровня");
    }
  };

  const getCompletedDocumentsData = async () => {
    try {
      const payload: any = {
        contragent: formRecords.contragent_id
          ? contractors.find((c) => c.contragent_id === +formRecords.contragent_id)?.contragent
          : undefined,
        status: [2],
      };
      const documents = await getDocumentsTableList(payload);
      setCompletedDocuments(documents.items);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении договоров");
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!formRecords[field]?.trim()) {
        newErrors[field] = true;
      }
    });

    if (!withoutContract && !formRecords["document_request_id"].trim()) {
      newErrors["document_request_id"] = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateExpenses = () => {
    return expenseTable.some(
      (expense) =>
        expense.name?.trim() && expense.quantity && expense.price && expense.budget && expense.budget_levels?.length
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const files = (e.target as HTMLInputElement).files;

    files && setFileList(files);

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (option: any, actionMeta: any) => {
    const { name } = actionMeta;
    const value = option?.value || "";

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleBudgetSelectChange = (option: any, index: number) => {
    const value = option?.value || "";
    handleExpenseChange(index, "budget", value);
  };

  const handleBudgetLevelSelectChange = (option: any, index: number, lvl_index: number) => {
    const value = option?.value || "";
    const newBudgetLevels = [...(expenseTable[index].budget_levels?.slice(0, lvl_index) || []), Number(value)];
    handleExpenseChange(index, "budget_levels", newBudgetLevels.join(","), lvl_index);
  };

  const submitPayment = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !validateExpenses()) {
      !validateExpenses() && notify("Заполните все поля в таблице расходов", "danger");
      return;
    }

    setFormLoading(true);
    try {
      const paymentDetails: any = [];
      const formData = new FormData();

      expenseTable.forEach((expense) => {
        let detail = {
          "Наименование товара": expense.name,
          Количество: expense.quantity,
          Цена: expense.price,
          Сумма: (expense.quantity || 0) * (expense.price || 0),
          "Статья из бюджета/Вне бюджета": "",
          budget_levels: [Number(expense.budget!), ...expense.budget_levels!],
        };
        paymentDetails.push(detail);
      });

      const paymentForm = {
        location_id: +formRecords.location_id,
        company_id: +formRecords.company_id,
        department_id: +formRecords.department_id,
        year: Number(formRecords.year) || moment().year(),
        template_id: +formRecords.template_id,
        choosen_location: user.locations.find((item) => item.location_id === +formRecords.location_id)?.location,
        template_name: userTemplates.find((item) => item.template_id === +formRecords.template_id)?.name,
        contragent_id: +formRecords.contragent_id,
        document_request_id: withoutContract ? null : +formRecords.document_request_id,
        project: formRecords.project,
        title: formRecords.title,
        details: paymentDetails,
        total_sum: totalSum,
        vid_oplaty: formRecords.vid_oplaty,
        budget_summa: totalBudgetBalance,
        ostatok_budgeta: totalBudgetBalance - totalSum,
        opisanie_rasxoda: formRecords.opisanie_rasxoda,
      };

      if (fileList) {
        Array.from(fileList).forEach((file) => {
          formData.append(`files`, file);
        });
      }

      const request_id = await createPaymentRequest(paymentForm);
      await uploadPaymentFiles(request_id, formData);
      notify("Заявка на оплату создана", "success");

      setFormSubmitted(true);
      const filters = new URLSearchParams();
      filters.append("locations", String(locationName));
      await getPaymentsData(undefined, filters);
      await getPaymentsTableData(undefined, filters);
      onClose?.();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при создании заявки на оплату");
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddRow = () => {
    setExpenseTable((prev) => [...prev, { ...emptyExpenseRow }]);
  };

  const handleDeleteRow = (index: number) => {
    setExpenseTable((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExpenseChange = async (index: number, field: keyof ExpenseItem, value: string, lvl_index?: number) => {
    setExpenseTable((prev) => {
      const newTable = [...prev];
      newTable[index] = {
        ...newTable[index],
        [field]:
          field === "budget_levels"
            ? value.split(",").map(Number)
            : field === "quantity" || field === "price" || field === "sum"
            ? value === ""
              ? undefined
              : Number(value)
            : value,
      };
      if (field === "budget") {
        newTable[index].budget_levels = [];
      }
      return newTable;
    });

    // Update available levels for this row
    if (field === "budget") {
      // When budget changes, fetch first level children
      if (value) {
        const tree = await getLevelTreeStructureData(Number(value));
        setAvailableLevelsByRow((prev) => ({
          ...prev,
          [index]: [[...(tree?.children || [])]],
        }));
      } else {
        setAvailableLevelsByRow((prev) => ({
          ...prev,
          [index]: [],
        }));
      }
    } else if (field === "budget_levels" && typeof lvl_index === "number") {
      // When a level changes, update next level's available children
      // Get the selected value for previous level
      const prevBudgetLevels = value.split(",").map(Number);
      let parentChildren: LevelType[] = [];
      // Find children of the selected level for this lvl_index
      const selectedLevelId = prevBudgetLevels[lvl_index];
      const currentLevels = availableLevelsByRow[index]?.[lvl_index] || [];
      const selectedLevel = currentLevels.find((lvl) => lvl.budget_type_id === selectedLevelId);
      parentChildren = selectedLevel?.children || [];

      setAvailableLevelsByRow((prev) => {
        const arr = prev[index] ? [...prev[index]] : [];
        // Set the next level's options to be the children of the selected level
        arr[lvl_index + 1] = parentChildren;
        // Remove all deeper levels
        return {
          ...prev,
          [index]: arr.slice(0, lvl_index + 2),
        };
      });
    }
  };

  const handleGetBudgetBalance = async () => {
    if (!formRecords.company_id || !formRecords.department_id || !formRecords.year) return;
    try {
      setFormLoading(true);

      const budgetsMap = new Map();
      // Create a map to store unique budget level combinations and their initial balances
      const budgetLevelMap = new Map();

      // Process each row sequentially
      for (let i = 0; i < expenseTable.length; i++) {
        const expense = expenseTable[i];
        if (!expense.budget || !expense.budget_levels?.length) continue;

        // Create a key for this combination of budget levels
        const levelKey = [expense.budget, ...expense.budget_levels].join("-");

        // If we already have this combination, use the remaining balance from previous calculation
        if (budgetLevelMap.has(levelKey)) {
          const prevBalance = budgetLevelMap.get(levelKey);
          // Update the expense table with the remaining balance
          setExpenseTable((prev) => {
            const newTable = [...prev];
            newTable[i] = {
              ...newTable[i],
              budget_balance: prevBalance,
            };
            // Update the map with the new remaining balance
            const newBalance = prevBalance - (expense.quantity || 0) * (expense.price || 0);
            budgetLevelMap.set(levelKey, newBalance);
            return newTable;
          });
        } else {
          // New combination - fetch the balance from API
          const balanceData = {
            company_id: +formRecords.company_id,
            department_id: +formRecords.department_id,
            year: +formRecords.year,
            budget_levels: [...expense.budget_levels.map(Number)],
          };

          const res: { remaining_budget_balance: number } = await getBudgetBalance(balanceData);

          // Store the remaining balance in the map
          const newBalance = res.remaining_budget_balance - (expense.quantity || 0) * (expense.price || 0);
          budgetsMap.set(levelKey, res.remaining_budget_balance);
          budgetLevelMap.set(levelKey, newBalance);

          // Update the expense table with the new balance
          setExpenseTable((prev) => {
            const newTable = [...prev];
            newTable[i] = {
              ...newTable[i],
              budget_balance: res.remaining_budget_balance,
            };
            return newTable;
          });
        }
      }

      setTotalBudgetBalance(Array.from(budgetsMap.values()).reduce((acc, val) => acc + val, 0));
      setBudgetCalculated(true);
      notify("Бюджет рассчитан", "success");
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при расчёте бюджета");
    } finally {
      setFormLoading(false);
    }
  };

  const expenseTableHeaders = [
    { header: "Наименование", narrow: false, wrap: false },
    { header: "Количество", narrow: false, wrap: true },
    { header: "Цена", narrow: false, wrap: false },
    { header: "Сумма", narrow: false, wrap: false },
    { header: "Название бюджета", narrow: false, wrap: false },
    ...(maxLevels > 0
      ? Array.from({ length: maxLevels }, (_, i) => ({
          header: `Уровень ${i + 1}`,
          required: true,
          narrow: false,
          wrap: false,
        }))
      : []),
    { header: "Сумма бюджета", narrow: false, wrap: false },
    { header: "Остаток бюджета", narrow: false, wrap: false },
    {
      header: (
        <button type="button" className="btn btn-ghost-primary" onClick={handleAddRow}>
          Добавить строку
        </button>
      ),
      narrow: true,
      wrap: false,
    },
  ];

  const renderField = ({ id, title, type, placeholder, options, disabled }: (typeof paymentFields)[number]) => {
    const Component = type === "textarea" ? "textarea" : "input";
    return (
      <div className={id === "opisanie_rasxoda" || id === "file" ? "col-lg-12" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label
            className={`form-label ${
              id === "budget_summa" || id === "ostatok_budgeta" || (id === "document_request_id" && withoutContract)
                ? ""
                : "required"
            }`}
          >
            {title}
          </label>
          <div className="input-icon mb-3">
            {type === "select" ? (
              <Select
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={options?.find((opt) => opt.value === formRecords[id])}
                onChange={handleSelectChange}
                options={options}
                isDisabled={isFormLoading || disabled}
                className={errors[id] ? "is-invalid" : ""}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            ) : type === "number" && id !== "year" ? (
              <FormattedNumberInput
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                value={
                  id === "budget_summa"
                    ? totalBudgetBalance
                    : id === "ostatok_budgeta"
                    ? totalBudgetBalance - totalSum
                    : formRecords[id] === "" || formRecords[id] === undefined
                    ? null
                    : Number(formRecords[id])
                }
                onChange={handleInputChange}
                disabled={isFormLoading || disabled}
              />
            ) : (
              <Component
                type={type}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                placeholder={placeholder}
                value={formRecords[id] || ""}
                onChange={handleInputChange}
                disabled={isFormLoading || disabled}
                multiple={type === "file"}
              />
            )}

            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>

          {id === "document_request_id" && (
            <div className="flex gap-2 mt-2">
              <input type="checkbox" checked={withoutContract} onChange={(e) => setWithoutContract(e.target.checked)} />
              <div className="form-label mb-0">Без договора</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const newPaymentBody = (
    <form id="paymentForm" className="row" onSubmit={submitPayment}>
      {paymentFields.slice(0, 9).map(renderField)}

      <div className="col-12 mb-3">
        <label className="form-label required">Детали оплаты</label>
        <div className="card rounded-2xl">
          <div className="table-responsive rounded-2xl">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  {expenseTableHeaders.map((header, index) => (
                    <th className={`${header.narrow && "w-1"} ${header.wrap && "text-wrap"}`} key={index}>
                      {header.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenseTable?.map((expense, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: 200 }}
                        name="name"
                        value={expense.name}
                        onChange={(e) => handleExpenseChange(index, "name", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="quantity"
                        value={expense.quantity || null}
                        onChange={(e) => handleExpenseChange(index, "quantity", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="price"
                        value={expense.price || null}
                        onChange={(e) => handleExpenseChange(index, "price", e.target.value)}
                      />
                    </td>
                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        name="sum"
                        value={(expense.quantity || 0) * (expense.price || 0) || null}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <Select
                        classNamePrefix="react-select"
                        placeholder="Выберите бюджет"
                        value={
                          levelRoots
                            .filter((lvl) => lvl.status)
                            .map((lvl) => ({
                              label: lvl.budget_type!,
                              value: lvl.budget_type_id!,
                              disabled: !!!lvl.status,
                            }))
                            .find((opt) => opt.value === expense.budget) || null
                        }
                        onChange={(option) => handleBudgetSelectChange(option, index)}
                        options={levelRoots
                          .filter((lvl) => lvl.status)
                          .map((lvl) => ({
                            label: lvl.budget_type!,
                            value: lvl.budget_type_id!,
                            disabled: !!!lvl.status,
                          }))}
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          container: (provided) => ({
                            ...provided,
                            width: 200,
                          }),
                        }}
                      />
                    </td>

                    {(maxLevels > 0 ? Array.from({ length: maxLevels }, (_, i) => ({ num: i + 1 })) : []).map(
                      (lvl, idx) => {
                        const availableLevels = availableLevelsByRow[index]?.[idx] || [];
                        return (
                          <td key={idx}>
                            {lvl.num <=
                            (levelRoots.find((lvl) => lvl.budget_type_id === Number(expenseTable[index].budget))
                              ?.max_levels || 0) ? (
                              <Select
                                classNamePrefix="react-select"
                                placeholder="Выберите уровень"
                                value={
                                  availableLevels
                                    .map((opt) => ({
                                      label: opt.budget_type || "",
                                      value: opt.budget_type_id?.toString() || "",
                                    }))
                                    .find(
                                      (opt) => opt.value === expenseTable[index].budget_levels?.[idx]?.toString()
                                    ) || null
                                }
                                onChange={(option) => handleBudgetLevelSelectChange(option, index, idx)}
                                options={
                                  availableLevels.map((opt) => ({
                                    label: opt.budget_type || "",
                                    value: opt.budget_type_id?.toString() || "",
                                  })) as any
                                }
                                isDisabled={isFormLoading || !expenseTable[index].budget}
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  container: (provided) => ({
                                    ...provided,
                                    width: 200,
                                  }),
                                }}
                              />
                            ) : (
                              <span className="text-secondary">Уровень {lvl.num} отстутствует</span>
                            )}
                          </td>
                        );
                      }
                    )}

                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        value={expense.budget_balance || 0}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>

                    <td>
                      <FormattedNumberInput
                        className="form-control"
                        style={{ width: 100 }}
                        value={expense.budget_balance! - expense.quantity! * expense.price! || 0}
                        onChange={() => undefined}
                        disabled
                      />
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <ActionButton
                          icon={<IconMinus className="icon" />}
                          title="Удалить строку"
                          action={() => handleDeleteRow(index)}
                          color={"danger"}
                          modal={false}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="col-lg-12 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-outline-primary mb-3"
          disabled={isFormLoading || !isBudgetCalculable}
          onClick={handleGetBudgetBalance}
        >
          Рассчитать бюджет
        </button>
      </div>

      {paymentFields.slice(9).map(renderField)}
    </form>
  );

  const newPaymentFooter = (
    <div className="d-flex gap-3">
      <div className="alert alert-primary m-0" style={{ padding: "7px 16px" }} role="alert">
        <div className="text-secondary">Перед отправкой заявки на оплату необходимо рассчитать бюджет</div>
      </div>

      {onClose && (
        <button type="button" className="btn btn-ghost-secondary" onClick={onClose} disabled={isFormLoading}>
          Отмена
        </button>
      )}

      <button
        type="submit"
        form="paymentForm"
        className="btn btn-primary d-flex gap-1"
        disabled={isFormLoading || !isBudgetCalculated}
      >
        Отправить заявку
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    </div>
  );

  useEffect(() => {
    if (isFormSubmitted && onClose) {
      // Close the modal after a brief delay to allow user to see success message
      const timer = setTimeout(() => {
        onClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted, onClose]);

  return (
    <ModalComponent
      header="Заявка на оплату"
      body={newPaymentBody}
      footer={newPaymentFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
